import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  Text,
} from '@chakra-ui/react'
import { RootState } from '../../app/store'
import { ChangeEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useApi from '../../common/utils/api/useApi'
import { AuthenticationClient } from '../../features/authentication/slice/authenticationSlice'
import InputField from '../InputField'
import { Logo } from '../shared-components/Logo'

interface SSOSamlAuthenticationModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const SSOSamlAuthenticationModal = ({
  isOpen,
  onClose,
  onSuccess,
}: SSOSamlAuthenticationModalProps) => {
  const email = useSelector((state: RootState) => state.authentication.email)
  const [verificationCode, setVerificationCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [isCountdownActive, setIsCountdownActive] = useState(false)
  const toast = useToast()
  const { trigger: sendEmailCode, loading: sendEmailCodeLoading } = useApi(
    AuthenticationClient.sendEmailCode,
    {
      onSuccess: async () => {
        toast({
          title: 'Email code sent successfully',
          status: 'success',
          duration: 3000,
          position: 'top',
          isClosable: true,
        })
        setCountdown(60)
        setIsCountdownActive(true)
      },
      onFailure: () => {
        toast({
          title: 'Failed to send email code, Please try again later',
          status: 'error',
          duration: 3000,
          position: 'top',
          isClosable: true,
        })
      },
    }
  )

  const { trigger: verifyEmailCode, loading: verifyEmailCodeLoading } = useApi(
    AuthenticationClient.verifyEmailCode,
    {
      onSuccess: () => {
        toast({
          title: 'Email code verified successfully',
          status: 'success',
          duration: 3000,
          position: 'top',
          isClosable: true,
        })
        onSuccess()
        onClose()
      },
      onFailure: () => {
        toast({
          title: 'Failed to verify email code, Please check your code and try again',
          status: 'error',
          duration: 3000,
          position: 'top',
          isClosable: true,
        })
      },
    }
  )

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isCountdownActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            setIsCountdownActive(false)
            return 0
          }
          return prevCountdown - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [isCountdownActive, countdown])

  useEffect(() => {
    sendEmailCode({})
  }, [])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent p={8}>
        <Logo maxWidth={100} />
        <ModalHeader mt={4}>
          A verification code has been sent to your email:
          <Text
            fontSize="sm"
            fontWeight="bold"
            fontFamily="mono"
            py={1}
            borderRadius="md"
            fontStyle="oblique"
            color="blue.500"
          >
            {email}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InputField
            label="Enter your six-digit verification code"
            field="verificationCode"
            isRequired
            mt={4}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value)}
          >
            <Input type="number" maxLength={6} placeholder="Enter verification code" />
          </InputField>
          <Text fontSize="sm" color="gray.600" mt={4}>
            Didn't receive a code?{' '}
            <Button
              onClick={() => sendEmailCode({})}
              variant="link"
              color="blue.500"
              cursor="pointer"
              isLoading={sendEmailCodeLoading}
              isDisabled={isCountdownActive || sendEmailCodeLoading}
            >
              {isCountdownActive ? `Resend code in ${countdown}s` : 'Resend code'}
            </Button>
          </Text>
          <Flex mt={8} justify="space-between">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              isLoading={verifyEmailCodeLoading}
              disabled={!verificationCode || verifyEmailCodeLoading}
              onClick={() => {
                verifyEmailCode({ code: verificationCode })
              }}
            >
              Continue
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default SSOSamlAuthenticationModal
